import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";
import { makeStyles } from "components/mui-core";
import InputLabel from "components-redesign/InputLabel";
import { endOfIsoDay } from "helpers";
import { ISOstring } from "model/modelTypes";
import React, { forwardRef, useCallback, useEffect, useState } from "react";
import {
  RANGE_ERRORS,
  dateErrorTextSx,
  displayToIso,
  isoToDisplay,
  outlinedDateTextFieldSx,
  validateDateInput,
} from "./utils";

const useStyles = makeStyles()({
  row: {
    display: "flex",
    gap: "12px",
    marginBottom: "8px",
  },
  textField: {
    flex: 1,
    ...outlinedDateTextFieldSx,
  },
  errorText: dateErrorTextSx,
});

interface Props {
  value: [ISOstring | null, ISOstring | null];
  onChange: (value: [ISOstring | null, ISOstring | null]) => void;
  onFocus: () => void;
  onBlur: () => void;
  label?: string;
  placeholder: string;
  showTime: boolean;
  error?: string;
  helperText?: string;
  disabled: boolean;
  disableStartDate: boolean;
  disableEndDate: boolean;
  minDate?: string;
  maxDate?: string;
}

const DateRangeInputs = forwardRef<HTMLDivElement, Props>(
  (
    {
      value,
      onChange,
      onFocus,
      onBlur,
      label,
      placeholder,
      showTime,
      error,
      helperText,
      disabled,
      disableStartDate,
      disableEndDate,
      minDate,
      maxDate,
    },
    ref
  ) => {
    const { classes: styles } = useStyles();
    const [startInput, setStartInput] = useState(() =>
      isoToDisplay(value[0], showTime)
    );
    const [endInput, setEndInput] = useState(() =>
      isoToDisplay(value[1], showTime)
    );
    const [startError, setStartError] = useState<string | null>(null);
    const [endError, setEndError] = useState<string | null>(null);

    useEffect(() => {
      setStartInput(isoToDisplay(value[0], showTime));
      setEndInput(isoToDisplay(value[1], showTime));
      setStartError(null);
      setEndError(null);
    }, [value, showTime]);

    const handleStartChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setStartInput(raw);
        const err = validateDateInput(
          raw,
          minDate,
          value[1] ?? maxDate,
          RANGE_ERRORS
        );
        setStartError(err);
        if (!err && raw) {
          const iso = displayToIso(raw);
          if (iso) onChange([iso, value[1]]);
        }
      },
      [minDate, maxDate, onChange, value]
    );

    const handleEndChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setEndInput(raw);
        const err = validateDateInput(
          raw,
          value[0] ?? minDate,
          maxDate,
          RANGE_ERRORS
        );
        setEndError(err);
        if (!err && raw) {
          const iso = displayToIso(raw);
          if (iso) {
            const userEnteredTime = /\d{2}:\d{2}/.test(raw);
            onChange([value[0], userEnteredTime ? iso : endOfIsoDay(iso)]);
          }
        }
      },
      [minDate, maxDate, onChange, value]
    );

    const timePlaceholder = showTime ? "DD/MM/YYYY HH:MM" : placeholder;

    return (
      <div>
        {label && <InputLabel error={!!error}>{label}</InputLabel>}
        <div
          ref={ref}
          className={styles.row}>
          <TextField
            variant="outlined"
            size="small"
            label={showTime ? "Start Date & Time" : "Start date"}
            value={startInput}
            onChange={handleStartChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={timePlaceholder}
            disabled={disabled || disableStartDate}
            error={!!startError || !!error}
            helperText={startError ? `\u26A0 ${startError}` : undefined}
            className={styles.textField}
            InputLabelProps={{ shrink: true }}
            FormHelperTextProps={{ className: styles.errorText }}
          />
          <TextField
            variant="outlined"
            size="small"
            label={showTime ? "End Date & Time" : "End Date"}
            value={endInput}
            onChange={handleEndChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={timePlaceholder}
            disabled={disabled || disableEndDate}
            error={!!endError || !!error}
            helperText={endError ? `\u26A0 ${endError}` : undefined}
            className={styles.textField}
            InputLabelProps={{ shrink: true }}
            FormHelperTextProps={{ className: styles.errorText }}
          />
        </div>
        {(helperText || error) && (
          <FormHelperText
            error={!!error}
            sx={{ marginLeft: 0 }}>
            {error || helperText}
          </FormHelperText>
        )}
      </div>
    );
  }
);

export default DateRangeInputs;
